import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

import { ContactModel } from '../../../../../core/lists';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

import { Document, SharingData } from '../../models';
import { SharingConfigKey, SharingMethod } from '../../enums';
import { DocumentService } from '../../services';

@Component({
  selector: 'kt-share-document',
  templateUrl: './share-document.component.html',
  styleUrls: ['./share-document.component.scss'],
})
export class ShareDocumentComponent implements OnInit {
  form: FormGroup;
  securityWord = '';
  sharingMethods = Object.values(SharingMethod);
  getDocumentAutocomplete = this.documentService.findDocuments.bind(this.documentService, {});
  getReceiverAutocomplete = this.documentService.findSharingReceivers.bind(this.documentService);
  @ViewChild('documentAutocomplete') documentAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('receiverAutocomplete') receiverAutocomplete: GeneralAutocompleteComponent;

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6] }],
      ['clean'],
    ],
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { document: Document; entitiesIds: number[] },
    public stringUtils: StringUtilsService,
    private dialogRef: MatDialogRef<ShareDocumentComponent>,
    private formBuilder: FormBuilder,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.documentService
      .getSharingConfigs()
      .pipe(take(1))
      .subscribe((res) => {
        this.securityWord =
          res?.data?.find((c) => c.key === SharingConfigKey.NYLAS_SECURITY_WORD)?.value || '';

        if (this.data.document?.id) {
          this.documentAutocomplete?.setValue(this.data.document);
          this.documentAutocomplete?.updateValueAndValidity();
        }
      });

    if (this.data.entitiesIds?.length) {
      this.getDocumentAutocomplete = this.documentService.findDocuments.bind(this.documentService, {
        entitiesIds: this.data.entitiesIds,
      });
    }

    this.form = this.formBuilder.group({
      methods: [null, Validators.required],
      body: [''],
    });
  }

  getReceiversFilter() {
    const methods = this.form.get('methods').value;

    return {
      documentId: this.documentAutocomplete?.value?.id,
      hasEmail: methods?.includes(SharingMethod.EMAIL),
      hasFax: methods?.includes(SharingMethod.FAX),
    };
  }

  handleChange(): void {
    this.getReceiverAutocomplete = this.documentService.findSharingReceivers.bind(
      this.documentService,
      this.getReceiversFilter(),
    );
  }

  formatDocumentOption(document: Document): string {
    return document?.name || '';
  }

  formatDocumentValue(document: Document): string {
    return `${document?.phi ? `[${this.securityWord}]` : ''} ${document?.name || ''}`.trim();
  }

  formatReceiverOption(receiver: ContactModel): string {
    return receiver?.id
      ? `<span class="${receiver.document_already_shared ? 'text-success' : ''}">
        ${`${receiver.first_name || ''} ${receiver.middle_name || ''} ${receiver.last_name || ''} ${
          receiver.last_name2 || ''
        }`.trim()}
      </span>`
      : '';
  }

  formatNewReceiver(receiver: ContactModel): ContactModel {
    return receiver;
  }

  isInvalid(name: string): boolean {
    const field = this.form.get(name);

    return field.touched && field.invalid;
  }

  getErrorMessage(label: string): string {
    return `${label} field is required`;
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.form.markAllAsTouched();
    this.documentAutocomplete.updateValueAndValidity();
    this.receiverAutocomplete.updateValueAndValidity();

    if (
      this.form.invalid ||
      this.documentAutocomplete.isInvalid() ||
      this.receiverAutocomplete.isInvalid()
    )
      return;

    const data: SharingData = {
      subject: this.formatDocumentValue(this.documentAutocomplete.value),
      document_id: this.documentAutocomplete.value.id,
      methods: this.form.get('methods').value,
      receivers: this.receiverAutocomplete.value.map((r) => r.id),
      body: this.form.get('body').value,
    };

    this.documentService
      .shareDocument(data)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.ok) return;

        this.dialogRef.close(true);
      });
  }
}
